import { useStripeData } from "@/hooks/useStripeData";
import { RefreshCw, Search, Filter, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export default function TransactionTable() {
  const { t } = useTranslation();
  const { transactions, isLoading, syncTransactions } = useStripeData();

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'succeeded': return 'bg-green-100 text-green-700 border-green-700';
      case 'failed': return 'bg-red-100 text-red-700 border-red-700';
      case 'refunded': return 'bg-gray-100 text-gray-700 border-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'succeeded': return t('admin.finance.transactions.status.succeeded');
      case 'failed': return t('admin.finance.transactions.status.failed');
      case 'refunded': return t('admin.finance.transactions.status.refunded');
      default: return status;
    }
  };

  return (
    <div className="rounded-2xl border-2 border-black bg-white shadow-[4px_4px_0_#000]">
      {/* Header */}
      <div className="border-b-2 border-black p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-xl font-black">{t('admin.finance.transactions.title')}</h3>
            <p className="text-sm font-bold text-gray-500">{t('admin.finance.transactions.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder={t('admin.finance.transactions.searchPlaceholder')}
                className="h-10 w-64 rounded-xl border-2 border-black pl-10 pr-4 font-bold focus:shadow-[2px_2px_0_#000] focus:outline-none"
              />
            </div>
            <button 
              onClick={() => syncTransactions.mutate()}
              disabled={syncTransactions.isPending}
              className="flex items-center gap-2 rounded-xl border-2 border-black bg-white px-4 py-2 font-bold hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={cn("h-4 w-4", syncTransactions.isPending && "animate-spin")} />
              {t('admin.finance.transactions.sync')}
            </button>
            <button className="flex items-center gap-2 rounded-xl border-2 border-black bg-[#FFD93D] px-4 py-2 font-bold shadow-[2px_2px_0_#000] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[3px_3px_0_#000] active:translate-x-0 active:translate-y-0 active:shadow-none">
              <Download className="h-4 w-4" />
              {t('admin.finance.transactions.export')}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b-2 border-black text-sm uppercase text-gray-500">
            <tr>
              <th className="px-6 py-4 font-black tracking-wider">{t('admin.finance.transactions.table.date')}</th>
              <th className="px-6 py-4 font-black tracking-wider">{t('admin.finance.transactions.table.user')}</th>
              <th className="px-6 py-4 font-black tracking-wider">{t('admin.finance.transactions.table.plan')}</th>
              <th className="px-6 py-4 font-black tracking-wider center">{t('admin.finance.transactions.table.amount')}</th>
              <th className="px-6 py-4 font-black tracking-wider">{t('admin.finance.transactions.table.status')}</th>
              <th className="px-6 py-4 font-black tracking-wider">{t('admin.finance.transactions.table.action')}</th>
            </tr>
          </thead>
          <tbody className="divide-y-2 divide-black/5">
            {isLoading ? (
                <tr><td colSpan={6} className="p-8 text-center font-bold text-gray-500">{t('admin.finance.transactions.table.loading')}</td></tr>
            ) : transactions?.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center font-bold text-gray-500">{t('admin.finance.transactions.table.empty')}</td></tr>
            ) : (
                transactions?.map((tx) => (
              <tr key={tx.id} className="hover:bg-gray-50/80 transition-colors">
                <td className="px-6 py-4 font-bold whitespace-nowrap">
                    {new Date(tx.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full border-2 border-black bg-[#4DA6FF] flex items-center justify-center font-black text-white text-xs">
                      ?
                    </div>
                    <span className="font-bold">{t('admin.finance.transactions.table.userLabel')}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-block rounded-lg bg-gray-100 px-2 py-1 text-xs font-bold border border-gray-300">
                    {tx.plan || tx.description}
                  </span>
                </td>
                <td className="px-6 py-4 font-mono font-bold">
                  {new Intl.NumberFormat('zh-TW', { style: 'currency', currency: tx.currency }).format(tx.amount / 100)}
                </td>
                <td className="px-6 py-4">
                  <span className={cn(
                    "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold",
                    getStatusStyle(tx.status)
                  )}>
                    {getStatusText(tx.status)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button className="text-sm font-bold text-gray-500 hover:text-black hover:underline">
                    {t('admin.finance.transactions.table.viewDetails')}
                  </button>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Mock */}
      <div className="border-t-2 border-black p-4 flex items-center justify-between">
        <span className="text-sm font-bold text-gray-500">
           {t('admin.finance.transactions.pagination.showing', { start: 1, end: 5, total: 128 })}
        </span>
        <div className="flex gap-2">
          <button className="rounded-lg border-2 border-black px-3 py-1 font-bold disabled:opacity-50" disabled>
            {t('admin.finance.transactions.pagination.prev')}
          </button>
          <button className="rounded-lg border-2 border-black px-3 py-1 font-bold hover:bg-gray-50">
            {t('admin.finance.transactions.pagination.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
